<template>
  <div>
    <Notification group="wrn-setup-notification" />
    <LoaderIndicator v-if="isLoading" class="background" with-container />
    <div v-if="!isLoading" class="background">
      <NavBar class="nav">
        <template #center>
          <BrandImage
            :image="EBrandImageLogos.SYMBOL_PRIMARY"
            width="24px"
            :schema="EBrandImageSchemas.POSITIVE"
            as="span"
          />
        </template>
      </NavBar>
      <div class="content">
        <Heading :level="1">Crie sua senha</Heading>
        <BaseText as="p" size="xl" :color="NebraskaColors.textSecondary">
          A senha deve ter no mínimo 8 caracteres.
        </BaseText>
        <div class="form">
          <InputText
            id="email"
            class="input"
            :value="email"
            label="Email"
            leading-icon="ic_mail"
            disabled
          />
          <InputPassword
            id="password"
            v-model="password"
            class="input"
            label="Senha"
            :password-strength="passwordScore"
            :password-strength-color="passwordStrengthColor"
            :helper-text="passwordMessage"
            @input="onInputChange"
          />
          <InputPassword
            id="confirm"
            v-model="passwordConfirm"
            class="input"
            label="Confirme sua senha"
          />
          <ButtonPrimary
            :disabled="!enableButton"
            :loading="comittingSetup"
            data-testid="submit-button"
            label="Criar Senha"
            size="large"
            @click="setupPassword"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

import { WarrenSession } from '@/modules/common/services/session';
import {
  ICommitSetupTokenResponse,
  checkSetupToken,
  commitSetupToken,
  getThemeByParam
} from '@/modules/common/services/http/account';

import {
  NavBar,
  BaseIcon,
  InputPassword,
  InputText,
  ButtonPrimary,
  Heading,
  BaseText,
  BrandImage,
  EBrandImageSchemas,
  EBrandImageLogos,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';

import { Notification } from '@/modules/common/components/Notification';

import { NotificationTypes } from '@/modules/common/services/http/notification';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { StorageKeys } from '@/modules/common/services/local-storage';
import { verifyPassword } from '@/modules/authentication/helpers';
import { debounce } from 'lodash-es';

@Component({
  components: {
    Notification,
    LoaderIndicator,
    NavBar,
    BaseIcon,
    InputPassword,
    InputText,
    ButtonPrimary,
    Heading,
    BaseText,
    BrandImage
  }
})
export default class PasswordSetupView extends Vue {
  private isLoading = true;
  private comittingSetup = false;

  private setupToken = '';

  private email = '';
  private password = '';
  private passwordConfirm = '';

  private passwordScore = 0;
  private passwordMessage = '';
  private isPasswordValid = false;
  private progressColor = NebraskaColors.statusInfo;

  readonly NebraskaColors = NebraskaColors;
  readonly EBrandImageLogos = EBrandImageLogos;
  readonly EBrandImageSchemas = EBrandImageSchemas;

  private async mounted() {
    this.setupToken = this.$route.params.token;

    await checkSetupToken({ hash: this.setupToken }).then(
      response => {
        this.isLoading = false;
        this.email = response.email;
      },
      _error => {
        this.isLoading = false;
        this.$notify({
          group: 'wrn-setup-notification',
          text: 'OPS! ESTA CHAVE DE ACESSO TEMPORÁRIA É INVÁLIDA',
          type: NotificationTypes.error
        });
      }
    );
  }

  private async setupPassword() {
    this.comittingSetup = true;
    await commitSetupToken({
      hash: this.setupToken,
      password: this.password
    }).then(
      response => {
        WarrenSession.set(StorageKeys.AccessToken, response.accessToken);
        WarrenSession.set(StorageKeys.CustomerId, response.user._id);
        response.user.clientType.b2bPartner
          ? this.loginPartner(response)
          : this.loginWhitelabel();
      },
      _error => {
        this.comittingSetup = false;
        this.$notify({
          group: 'wrn-setup-notification',
          text: 'Não foi possivel registrar a senha.',
          type: NotificationTypes.error
        });
      }
    );
  }

  private loginWhitelabel() {
    WarrenSession.set(StorageKeys.WhitelabelTheme, 'whitelabel');
    // eslint-disable-next-line promise/catch-or-return
    getThemeByParam('email', this.email)
      .then(theme =>
        localStorage.setItem(StorageKeys.Theme, JSON.stringify(theme))
      )
      .finally(() => this.$router.push({ name: 'v3' }));
  }

  private loginPartner(data: ICommitSetupTokenResponse) {
    WarrenSession.set(StorageKeys.Partner, JSON.stringify(data.user));
    WarrenSession.set(StorageKeys.IsPartnerAccountSession, 'true');
    this.$router.push({ name: 'v3' });
  }

  private get enableButton() {
    return this.isPasswordValid && this.password === this.passwordConfirm;
  }

  private debounceVerifyPassword = debounce(this.verifyPassword, 300);

  onInputChange() {
    this.isPasswordValid = false;
    this.debounceVerifyPassword();
  }

  private async verifyPassword() {
    const response = await verifyPassword(this.password);
    if (response) {
      this.passwordScore = response.passwordScore;
      this.passwordMessage = response.passwordMessage;
      this.isPasswordValid = response.isPasswordValid;
      this.progressColor = response.progressColor;
    }
  }

  private get passwordStrengthColor() {
    if (this.passwordScore <= 0.1) {
      return NebraskaColors.statusNegative;
    } else {
      return this.progressColor;
    }
  }
}
</script>

<style lang="less" scoped>
.nav {
  background-color: @background-secondary;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);

  .form {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 320px;
    margin-top: @size-spacing-x600;

    .input {
      width: 100%;
      margin-bottom: @size-spacing-x350;
    }
  }
}

@media only screen and (max-width: 480px) {
  .form {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: calc(320px - @size-spacing-x500);
    padding: 0 @size-spacing-x500;
  }
}
</style>
